import { useRef, useState, type ReactNode } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: ReactNode;
}

const THRESHOLD = 64;
const MAX_PULL = 90;

/**
 * Touch pull-to-refresh, mirroring the native RefreshIndicator on the
 * load/unload lists. Only engages when the page is scrolled to the very top and
 * the user drags downward; no-ops with mouse/desktop.
 */
export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const atTop = () => (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = atTop() ? e.touches[0].clientY : null;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0 && atTop()) {
      setPull(Math.min(dy * 0.5, MAX_PULL));
    } else {
      setPull(0);
    }
  };

  const onTouchEnd = async () => {
    if (pull >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }
    setPull(0);
    startY.current = null;
  };

  const showIndicator = pull > 4 || refreshing;

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={() => void onTouchEnd()}>
      {showIndicator && (
        <div
          className="flex items-center justify-center overflow-hidden text-brand-muted transition-[height]"
          style={{ height: refreshing ? 40 : pull }}
        >
          {refreshing ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ArrowDown
              className="h-5 w-5 transition-transform"
              style={{ transform: pull >= THRESHOLD ? 'rotate(180deg)' : 'none' }}
            />
          )}
        </div>
      )}
      {children}
    </div>
  );
}
